import React, { createContext, useContext, useState, useCallback } from "react";
import UI_STRINGS from "../locales/commons.json";
import campusConfig from "../json/campus_config.json";

const LanguageContext = createContext();

export const LANG = {
  JA: "ja",
  EN: "en",
};
export const IS_MULTILINGUAL_ENABLED =
  process.env.REACT_APP_ENABLE_MULTILINGUAL !== "false";

/**
 * 言語に応じてフィールドを返す（マップで使用する各大学の建物やキャンパスの名称などの多言語対応に使用）
 * lang === "en" の場合、 obj[field + "_en"] を返す（存在し、空でない場合）。
 * フォールバックとして obj[field] を返す。
 *
 * @param {Object} obj    - 対象オブジェクト (例: building)
 * @param {string} field  - ベースフィールド名 (例: "name")
 * @param {string} lang   - 言語コード ("ja" | "en")
 * @returns {string}
 */
export function localizedField(obj, field, lang) {
  if (!obj) return "";
  if (lang === LANG.EN) {
    const enValue = obj[`${field}_en`];
    if (enValue !== undefined && enValue !== null && enValue !== "") {
      return enValue;
    }
  }
  return obj[field] ?? "";
}

/**
 * UI文字列を取得する(マップで使用する共通UIの多言語対応)
 * @param {string} key  - UIStringのキー
 * @param {string} lang - 言語コード
 * @returns {string}
 */
export function t(key, lang) {
  return (
    campusConfig.localeOverrides?.[lang]?.[key] ??
    campusConfig.localeOverrides?.[LANG.JA]?.[key] ??
    UI_STRINGS[lang]?.[key] ??
    UI_STRINGS[LANG.JA]?.[key] ??
    key
  );
}

export function LanguageProvider({ children }) {
  const [languageState, setLanguageState] = useState(LANG.JA);

  const language = IS_MULTILINGUAL_ENABLED ? languageState : LANG.JA;

  const setLanguage = useCallback((nextLanguage) => {
    if (!IS_MULTILINGUAL_ENABLED) return;
    setLanguageState(nextLanguage);
  }, []);

  const toggleLanguage = useCallback(() => {
    if (!IS_MULTILINGUAL_ENABLED) return;
    setLanguageState((prev) => (prev === LANG.JA ? LANG.EN : LANG.JA));
  }, []);

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        toggleLanguage,
        isMultilingualEnabled: IS_MULTILINGUAL_ENABLED,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}

export default LanguageContext;
